import { Box, MenuItem, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  MessagingProviderSmallIcon20,
  isWappMsgProvider,
} from '@src/model/frontendmodel';
import { selectContactBotAccounts } from '@src/selectors/contact';
import type { ContactBotAccount } from '@src/types';
import StringUtils from '@src/utils/strings';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  providerIcon: {
    width: 18,
    height: 18,
    flexShrink: 0,
  },
  accountName: {
    flex: 1,
    fontSize: '0.85rem',
  },
  warningContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.warning.main,
  },
  warningIcon: {
    width: 16,
    height: 16,
  },
  disabledText: {
    color: theme.palette.text.disabled,
    fontSize: '0.85rem',
  },
}));

interface BotAccountSelectProps {
  value: ContactBotAccount | null;
  onChange: (account: ContactBotAccount | null) => void;
  contactAccountChecked: boolean | null;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
}

const ContactBotAccountSelect: React.FC<BotAccountSelectProps> = ({
  value,
  onChange,
  contactAccountChecked,
  disabled = false,
  error = false,
  helperText,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const botAccounts = useSelector(selectContactBotAccounts);
  const selectedBotAcccountId = useMemo(
    () => value?.botAccountId ?? null,
    [value]
  );

  const onChangeSelectedBotAccount = useCallback(
    (botAccountId: number) => {
      const selectedBotAccount = botAccounts?.find(
        account => account.botAccountId === botAccountId
      );

      onChange(selectedBotAccount ?? null);
    },
    [onChange, botAccounts]
  );

  const renderContactBotAccountItem = (account: ContactBotAccount) => {
    const accountAlias = StringUtils.trimToEmpty(account.botAccountAlias);
    const accountName = !accountAlias
      ? `${account.botAccount}@${account.provider}`
      : `(${accountAlias}) ${account.botAccount}@${account.provider}`;

    const logoSrc =
      account.botAccountIconUrl ||
      MessagingProviderSmallIcon20[account.provider];

    const isWapp = isWappMsgProvider(account.provider);
    const isSuspended = account.botStatus === 'SUSPENDED';
    // const showWarning = isWapp && !contactAccountChecked;

    return (
      <Box className={classes.menuItem}>
        <img
          className={classes.providerIcon}
          src={logoSrc}
          alt={account.provider}
        />
        <span className={classes.accountName}>{accountName}</span>

        {isWapp && contactAccountChecked && isSuspended && (
          <span className={classes.disabledText}>
            {formatMessage({
              id: 'Contact.startChatDialog.itemDisabledTemporarily',
            })}
          </span>
        )}
      </Box>
    );
  };

  // If no accounts available, don't render
  if (!botAccounts || botAccounts.length === 0) {
    return null;
  }

  return (
    <TextField
      select
      fullWidth
      id="botAccount"
      value={selectedBotAcccountId ?? ''}
      label={
        <FormattedMessage id="Contact.startChatDialog.selectBotAccountLabel" />
      }
      onChange={({ target }) =>
        onChangeSelectedBotAccount(Number(target.value))
      }
      error={error}
      helperText={helperText}
      disabled={disabled}
    >
      {botAccounts.map(account => {
        const isDisabled = account.botStatus === 'SUSPENDED';

        return (
          <MenuItem
            value={account.botAccountId}
            key={account.botAccountId}
            disabled={isDisabled}
          >
            {renderContactBotAccountItem(account)}
          </MenuItem>
        );
      })}
    </TextField>
  );
};

export default ContactBotAccountSelect;
